import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getRuntimeConfig, setRuntimeConfig } from "@/lib/api";
import { validateTrialData, type TrialData, type TrialIssue, type TrialStage } from "@/lib/trial-schema";
import { diffTrialData } from "@/lib/trial-diff";
import { useDirtyStore } from "@/lib/dirty-store";
import { FloorList } from "@/components/trial/FloorList";
import { FloorCard } from "@/components/trial/FloorCard";
import { issuesForStage } from "@/lib/trial-issues";
import { addFloor, removeLastFloor, updateStageAt } from "@/lib/trial-edit";
import { LeaderboardPanel } from "@/routes/leaderboard";

const TRIAL_KEY = "EC2_TRIAL_STAGE_3.0";

function prettify(raw: string): string {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function TrialRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "leaderboard" ? "leaderboard" : "floors";

  function selectTab(next: "floors" | "leaderboard") {
    const params = new URLSearchParams(searchParams);
    if (next === "leaderboard") params.set("tab", "leaderboard");
    else params.delete("tab");
    setSearchParams(params);
  }

  return (
    <AppShell title="Chamber of Trial" subtitle="Configure floors and manage the trial leaderboard">
      <div className="flex gap-2 mb-5" role="tablist" aria-label="Chamber of Trial sections">
        <Button role="tab" aria-selected={tab === "floors"} variant={tab === "floors" ? "secondary" : "ghost"} onClick={() => selectTab("floors")}>Floors</Button>
        <Button role="tab" aria-selected={tab === "leaderboard"} variant={tab === "leaderboard" ? "secondary" : "ghost"} onClick={() => selectTab("leaderboard")}>Leaderboard</Button>
      </div>
      <div role="tabpanel" aria-label="Floors" hidden={tab !== "floors"}>
        <TrialFloorEditor />
      </div>
      {tab === "leaderboard" && <div role="tabpanel" aria-label="Leaderboard"><LeaderboardPanel /></div>}
    </AppShell>
  );
}

function TrialFloorEditor() {
  const qc = useQueryClient();
  const setDirty = useDirtyStore((s) => s.setDirty);

  const query = useQuery({
    queryKey: ["runtime-config", TRIAL_KEY],
    queryFn: () => getRuntimeConfig(TRIAL_KEY),
  });

  const serverValue = query.data ?? "";

  // The serialized form state. It is seeded from the server once it loads;
  // form edits stay local until Save round-trips.
  const [text, setText] = useState("");
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && query.isSuccess) {
      setText(prettify(serverValue));
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, seeded]);

  const dirty = seeded && text !== prettify(serverValue);
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // `data` is the form's single source of truth. Each form edit produces a
  // new object and serializes it directly back into `text`, so the saved
  // payload cannot drift from what the form displays.
  const { parseError, issues, data } = useMemo(() => {
    if (!text.trim()) {
      return { parseError: "Empty document.", issues: [] as TrialIssue[], data: null as TrialData | null };
    }
    try {
      const parsed = JSON.parse(text) as TrialData;
      return { parseError: null, issues: validateTrialData(parsed), data: parsed };
    } catch (e) {
      return {
        parseError: e instanceof Error ? e.message : "Invalid JSON",
        issues: [] as TrialIssue[],
        data: null as TrialData | null,
      };
    }
  }, [text]);

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const canSave = dirty && !parseError && errors.length === 0;

  const [selectedFloorIdx, setSelectedFloorIdx] = useState(0);
  useEffect(() => {
    if (!data) return;
    if (selectedFloorIdx >= data.stages.length) {
      setSelectedFloorIdx(Math.max(0, data.stages.length - 1));
    }
  }, [data, selectedFloorIdx]);

  function commitData(next: TrialData) {
    setText(JSON.stringify(next, null, 2));
  }

  function updateStage(idx: number, updater: (s: TrialStage) => TrialStage) {
    if (!data) return;
    commitData(updateStageAt(data, idx, updater));
  }

  function handleAddFloor() {
    if (!data) return;
    const next = addFloor(data);
    commitData(next);
    setSelectedFloorIdx(next.stages.length - 1);
  }

  function handleRemoveLastFloor() {
    if (!data || data.stages.length === 0) return;
    const lastFloor = data.stages[data.stages.length - 1].floor;
    if (!window.confirm(`Remove floor ${lastFloor}? This can only be undone by reverting to live.`)) return;
    commitData(removeLastFloor(data));
  }

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const diff = useMemo(() => diffTrialData(serverValue, text), [serverValue, text]);

  const mutation = useMutation({
    mutationFn: () => setRuntimeConfig(TRIAL_KEY, JSON.stringify(JSON.parse(text))),
    onSuccess: async () => {
      setShowConfirm(false);
      setConfirmText("");
      await qc.invalidateQueries({ queryKey: ["runtime-config", TRIAL_KEY] });
      // Re-seed from the freshly-saved value so `dirty` clears.
      setSeeded(false);
    },
  });

  function openConfirm() {
    setConfirmText("");
    setShowConfirm(true);
  }

  return (
    <>
      {query.isLoading && (
        <div className="space-y-2 max-w-3xl">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {query.isError && (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertDescription>
            Failed to load {TRIAL_KEY} from D1: {String(query.error)}. Import this fixed runtime-config
            key from PlayFab before using the panel.
          </AlertDescription>
        </Alert>
      )}

      {query.isSuccess && (
        <div className="space-y-3">
          {data ? (
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
              <FloorList
                stages={data.stages}
                issues={issues}
                selected={selectedFloorIdx}
                onSelect={setSelectedFloorIdx}
                onAddFloor={handleAddFloor}
                onRemoveLastFloor={handleRemoveLastFloor}
              />
              <div className="min-w-0 max-h-[75vh] overflow-y-auto pr-1">
                {data.stages[selectedFloorIdx] ? (
                  <FloorCard
                    stage={data.stages[selectedFloorIdx]}
                    issues={issuesForStage(issues, selectedFloorIdx)}
                    onUpdate={(updater) => updateStage(selectedFloorIdx, updater)}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No floors yet — add one from the list.</p>
                )}
              </div>
            </div>
          ) : (
            <Alert variant="destructive" className="max-w-2xl">
              <AlertDescription>
                The Chamber data could not be opened in the form editor: {parseError ?? "Unknown error"}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1" />
            <Badge variant={errors.length ? "destructive" : "secondary"}>
              {errors.length} error{errors.length === 1 ? "" : "s"}
            </Badge>
            <Badge variant="secondary">
              {warnings.length} warning{warnings.length === 1 ? "" : "s"}
            </Badge>
            <Button onClick={openConfirm} disabled={!canSave}>
              Review &amp; save
            </Button>
          </div>
        </div>
      )}

      {showConfirm && (
        <ConfirmSaveDialog
          diff={diff}
          onCancel={() => setShowConfirm(false)}
          confirmText={confirmText}
          onConfirmTextChange={setConfirmText}
          saving={mutation.isPending}
          error={mutation.error}
          onConfirm={() => mutation.mutate()}
        />
      )}
    </>
  );
}

function ConfirmSaveDialog({
  diff,
  onCancel,
  onConfirm,
  confirmText,
  onConfirmTextChange,
  saving,
  error,
}: {
  diff: ReturnType<typeof diffTrialData>;
  onCancel: () => void;
  onConfirm: () => void;
  confirmText: string;
  onConfirmTextChange: (v: string) => void;
  saving: boolean;
  error: unknown;
}) {
  const confirmed = confirmText.trim() === TRIAL_KEY;
  const noChanges = !diff.unparseable && !diff.leaderboardVersionChanged && diff.floors.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.currentTarget === e.target) onCancel();
      }}
    >
      <div className="w-full max-w-3xl rounded-lg border border-border bg-card flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-medium">Confirm write to {TRIAL_KEY}</h2>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              This repo has one PlayFab title and no staging/prod split — this write goes straight to
              production. Review the diff below before confirming.
            </AlertDescription>
          </Alert>

          {diff.leaderboardVersionChanged && (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">
                <strong>leaderboardVersion is changing</strong> ({diff.leaderboardVersionBefore ?? "?"} →{" "}
                {diff.leaderboardVersionAfter ?? "?"}). <code>AssignTrialStage</code> calls{" "}
                <code>ResetProgression()</code> whenever this value doesn't match what a player last saw —
                this wipes <strong>every player's cleared floors, stars, and best times</strong> for the
                Chamber of Trial. This is a season reset, not a version bump. Confirm this is intentional.
              </AlertDescription>
            </Alert>
          )}

          {diff.unparseable ? (
            <p className="text-xs text-muted-foreground">Could not compute a floor-level diff.</p>
          ) : noChanges ? (
            <p className="text-xs text-muted-foreground">No floors changed. This is a no-op write.</p>
          ) : (
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-foreground/80">
                {diff.floors.length} floor{diff.floors.length === 1 ? "" : "s"} changed
              </h3>
              {diff.floors.map((f) => (
                <div key={f.floor} className="rounded-md border border-border overflow-hidden">
                  <div className="px-3 py-1.5 text-xs font-medium bg-accent/40 flex items-center gap-2">
                    Floor {f.floor}
                    <Badge variant="secondary">{f.kind}</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                    <pre className="text-[10px] p-2 overflow-auto max-h-56 bg-destructive/5">
                      {f.before ?? "(none)"}
                    </pre>
                    <pre className="text-[10px] p-2 overflow-auto max-h-56 bg-emerald-500/5">
                      {f.after ?? "(none)"}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error != null && (
            <Alert variant="destructive">
              <AlertDescription>{String(error)}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5 pt-2">
            <label className="text-xs text-muted-foreground">
              Type <code className="font-mono">{TRIAL_KEY}</code> to confirm this write:
            </label>
            <input
              value={confirmText}
              onChange={(e) => onConfirmTextChange(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              placeholder={TRIAL_KEY}
              autoFocus
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!confirmed || saving}>
            {saving ? "Writing…" : "Write to PlayFab"}
          </Button>
        </div>
      </div>
    </div>
  );
}
